import { useState, useCallback, useEffect, useMemo } from 'react'
import { Modal, ModalDialog, ModalClose, Typography, Divider, LinearProgress, Stack, Input, Button, Chip, Alert, IconButton, Sheet, Switch } from '@mui/joy'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'

import {
    listWatched,
    recentDialogsApi,
    joinChatByLink,
    addWatchedChat,
    removeWatchedChat,
    updateChatBrokerName,
    watchAllChats,
    unwatchAllChats,
} from '../../../api/telegramApi'
import { type TelegramAccount, type WatchedChat, type Dialog } from '../types'

interface ManageWatchModalProps {
    open: boolean
    phone: string
    accounts: TelegramAccount[]
    onClose: () => void
    onWatchedMapUpdate: (phone: string, items: WatchedChat[]) => void
}

export function ManageWatchModal({ open, phone, accounts, onClose, onWatchedMapUpdate }: ManageWatchModalProps): JSX.Element {
    const [loadingDialogs, setLoadingDialogs] = useState(false)
    const [watchedChats, setWatchedChats] = useState<WatchedChat[]>([])
    const [dialogs, setDialogs] = useState<Dialog[]>([])
    const [searchQuery, setSearchQuery] = useState('')
    const [joiningLink, setJoiningLink] = useState('')
    const [loadingJoin, setLoadingJoin] = useState(false)
    const [modalMessage, setModalMessage] = useState<string | null>(null)
    const [modalError, setModalError] = useState<string | null>(null)
    const [editingBrokerPeerId, setEditingBrokerPeerId] = useState<string | null>(null)
    const [editingBrokerName, setEditingBrokerName] = useState('')
    const [savingBrokerName, setSavingBrokerName] = useState(false)

    const resetModalAlerts = useCallback(() => {
        setModalMessage(null)
        setModalError(null)
    }, [])

    useEffect(() => {
        if (modalMessage || modalError) {
            const timer = setTimeout(resetModalAlerts, 5000)
            return () => clearTimeout(timer)
        }
    }, [modalMessage, modalError, resetModalAlerts])

    const account = useMemo(() => accounts.find((a) => a.phone === phone), [accounts, phone])
    const isActive = account?.status === 'active'

    useEffect(() => {
        if (!open) return

        const loadData = async () => {
            resetModalAlerts()
            setSearchQuery('')
            setJoiningLink('')
            setEditingBrokerPeerId(null)
            setEditingBrokerName('')
            setLoadingDialogs(true)
            try {
                const items = await listWatched(phone)
                setWatchedChats(items)

                const dialogsRes = await recentDialogsApi(phone, 100)
                if (dialogsRes?.ok) {
                    setDialogs(dialogsRes.data || [])
                }
            } catch (e) {
                setModalError((e as Error)?.message || 'Failed to load dialogs')
            } finally {
                setLoadingDialogs(false)
            }
        }

        void loadData()
    }, [open, phone, resetModalAlerts])

    const handleWatchedUpdate = useCallback(
        (items: WatchedChat[]) => {
            setWatchedChats(items)
            onWatchedMapUpdate(phone, items)
        },
        [phone, onWatchedMapUpdate]
    )

    const handleJoinAndWatch = useCallback(async () => {
        if (!joiningLink.trim()) return
        setLoadingJoin(true)
        try {
            const result = await joinChatByLink(phone, joiningLink.trim())
            if (!result?.ok) throw new Error(result?.error || 'Failed to join group/channel')

            const chatData = result.data || {}
            if (!chatData.peerId) throw new Error('Could not retrieve group/channel information')

            await addWatchedChat({
                phone,
                peerId: chatData.peerId,
                username: chatData.username,
                title: chatData.title,
                isChannel: chatData.isChannel,
            })

            const groupName = chatData.title || chatData.username || 'group/channel'
            setModalMessage(`Joined "${groupName}" and added to watch list`)
            setJoiningLink('')

            const items = await listWatched(phone)
            handleWatchedUpdate(items)

            const dialogsRes = await recentDialogsApi(phone, 100)
            if (dialogsRes?.ok) setDialogs(dialogsRes.data || [])
        } catch (e) {
            setModalError((e as Error)?.message || 'Failed to join group/channel. Please check the link and try again.')
        } finally {
            setLoadingJoin(false)
        }
    }, [phone, joiningLink, handleWatchedUpdate])

    const handleSaveBrokerName = useCallback(
        async (peerId: string) => {
            setSavingBrokerName(true)
            try {
                const brokerName = editingBrokerName.trim() || null
                await updateChatBrokerName(phone, peerId, brokerName)
                setModalMessage(brokerName ? `Broker name updated to "${brokerName}"` : 'Broker name cleared')
                setEditingBrokerPeerId(null)
                setEditingBrokerName('')

                const items = await listWatched(phone)
                handleWatchedUpdate(items)
            } catch (e) {
                setModalError((e as Error)?.message || 'Failed to update broker name')
            } finally {
                setSavingBrokerName(false)
            }
        },
        [phone, editingBrokerName, handleWatchedUpdate]
    )

    const dialogsMap = useMemo(() => new Map(dialogs.map((d) => [d.peerId, d])), [dialogs])

    const { watchedFiltered, unwatchedFiltered } = useMemo(() => {
        const searchLower = searchQuery.toLowerCase()

        const watched = watchedChats.filter((w) => {
            if (searchQuery) {
                const titleMatch = w.title?.toLowerCase().includes(searchLower)
                const usernameMatch = w.username?.toLowerCase().includes(searchLower)
                return titleMatch || usernameMatch
            }
            return true
        })

        const watchedSet = new Set(watchedChats.map((w) => w.peerId))
        const unwatched = dialogs.filter((d) => {
            if (d.isUser) return false
            if (watchedSet.has(d.peerId)) return false

            if (searchQuery) {
                const titleMatch = d.title?.toLowerCase().includes(searchLower)
                const usernameMatch = d.username?.toLowerCase().includes(searchLower)
                return titleMatch || usernameMatch
            }
            return true
        })

        return { watchedFiltered: watched, unwatchedFiltered: unwatched }
    }, [watchedChats, dialogs, searchQuery])

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog variant="outlined" sx={{ borderRadius: 'xl', minWidth: 700, maxWidth: 900, maxHeight: '90vh' }}>
                <ModalClose />
                <Typography level="h4" component="h2">
                    Manage Watched Groups
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        Account:
                    </Typography>
                    <Chip size="sm" variant="soft">
                        {phone}
                    </Chip>
                    {account && account.status !== 'active' && (
                        <Chip size="sm" variant="soft" color="warning">
                            Account {account.status}
                        </Chip>
                    )}
                </Stack>
                <Divider sx={{ my: 2 }} />

                {loadingDialogs && <LinearProgress sx={{ mb: 2 }} />}

                {modalMessage && (
                    <Alert
                        color="success"
                        variant="soft"
                        sx={{ mb: 2 }}
                        endDecorator={
                            <IconButton size="sm" variant="plain" color="success" onClick={resetModalAlerts}>
                                <CloseIcon />
                            </IconButton>
                        }
                    >
                        {modalMessage}
                    </Alert>
                )}
                {modalError && (
                    <Alert
                        color="danger"
                        variant="soft"
                        sx={{ mb: 2 }}
                        endDecorator={
                            <IconButton size="sm" variant="plain" color="danger" onClick={resetModalAlerts}>
                                <CloseIcon />
                            </IconButton>
                        }
                    >
                        {modalError}
                    </Alert>
                )}

                {account && account.status !== 'active' && (
                    <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                        This account is {account.status}. You cannot watch new groups or modify settings until you log in again.
                    </Alert>
                )}

                <Stack spacing={2}>
                    {/* Join by link */}
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', bgcolor: 'background.level1', opacity: isActive ? 1 : 0.6 }}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>
                            Join New Group/Channel
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Input
                                placeholder="Paste invite link or @username"
                                value={joiningLink}
                                onChange={(e) => setJoiningLink(e.target.value)}
                                sx={{ flex: 1 }}
                                disabled={loadingJoin || !isActive}
                            />
                            <Button variant="solid" disabled={!isActive || !joiningLink.trim()} onClick={handleJoinAndWatch} loading={loadingJoin}>
                                Join & Watch
                            </Button>
                        </Stack>
                    </Sheet>

                    {/* Search */}
                    <Input
                        placeholder="Search groups and channels..."
                        startDecorator={<SearchIcon />}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="lg"
                    />

                    {/* Groups List */}
                    <Stack spacing={1} sx={{ maxHeight: '50vh', overflow: 'auto', pr: 1 }}>
                        {watchedFiltered.length === 0 && unwatchedFiltered.length === 0 ? (
                            <Typography level="body-sm" sx={{ textAlign: 'center', py: 4, color: 'text.tertiary' }}>
                                {searchQuery ? 'No results found' : 'No groups or channels available'}
                            </Typography>
                        ) : (
                            <>
                                {/* Watched */}
                                {watchedFiltered.length > 0 && (
                                    <>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                            <Typography level="title-sm" sx={{ color: 'success.500' }}>
                                                Watching ({watchedFiltered.length})
                                            </Typography>
                                            <Button
                                                size="sm"
                                                variant="outlined"
                                                color="danger"
                                                disabled={!isActive}
                                                onClick={async () => {
                                                    resetModalAlerts()
                                                    try {
                                                        const result = await unwatchAllChats(phone)
                                                        if (!result?.ok) throw new Error(result?.error || 'Failed to unwatch all')
                                                        const removed = result.data?.removed || 0
                                                        setModalMessage(`Removed ${removed} group(s)/channel(s) from watch list`)
                                                        const items = await listWatched(phone)
                                                        handleWatchedUpdate(items)
                                                    } catch (e) {
                                                        setModalError((e as Error)?.message || 'Failed to unwatch all')
                                                    }
                                                }}
                                            >
                                                Unwatch All
                                            </Button>
                                        </Stack>
                                        {watchedFiltered.map((w) => {
                                            const dialogInfo = dialogsMap.get(w.peerId)
                                            const d = dialogInfo || {
                                                peerId: w.peerId,
                                                title: w.title,
                                                username: w.username,
                                                isChannel: w.isChannel,
                                                isGroup: !w.isChannel,
                                            }
                                            const isEditingBroker = editingBrokerPeerId === w.peerId
                                            return (
                                                <Sheet
                                                    key={d.peerId}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 'md',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1,
                                                        borderColor: 'success.outlinedBorder',
                                                        bgcolor: 'success.softBg',
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="center" gap={2}>
                                                        <Stack sx={{ flex: 1 }}>
                                                            <Typography level="title-sm">{d.title || d.username || 'Untitled'}</Typography>
                                                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                                                                {d.username && (
                                                                    <Chip size="sm" variant="soft" color="primary">
                                                                        @{d.username}
                                                                    </Chip>
                                                                )}
                                                                <Chip size="sm" variant="soft">
                                                                    {d.isChannel ? 'Channel' : 'Group'}
                                                                </Chip>
                                                            </Stack>
                                                        </Stack>
                                                        <Switch
                                                            checked={true}
                                                            disabled={!isActive}
                                                            onChange={async () => {
                                                                if (!isActive) return
                                                                resetModalAlerts()
                                                                try {
                                                                    await removeWatchedChat(phone, d.peerId)
                                                                    const groupName = d.title || d.username || 'group'
                                                                    setModalMessage(`Removed "${groupName}" from watch list`)
                                                                    const items = await listWatched(phone)
                                                                    handleWatchedUpdate(items)
                                                                } catch (e) {
                                                                    setModalError((e as Error)?.message || 'Failed to remove from watch list')
                                                                }
                                                            }}
                                                        />
                                                    </Stack>
                                                    <Stack direction="row" alignItems="center" gap={1} sx={{ pl: 0.5 }}>
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', minWidth: 45 }}>
                                                            Broker:
                                                        </Typography>
                                                        {isEditingBroker ? (
                                                            <>
                                                                <Input
                                                                    size="sm"
                                                                    placeholder="Enter broker name..."
                                                                    value={editingBrokerName}
                                                                    onChange={(e) => setEditingBrokerName(e.target.value)}
                                                                    sx={{ flex: 1, maxWidth: 200 }}
                                                                    disabled={savingBrokerName}
                                                                />
                                                                <Button
                                                                    size="sm"
                                                                    variant="solid"
                                                                    color="primary"
                                                                    onClick={() => handleSaveBrokerName(w.peerId)}
                                                                    loading={savingBrokerName}
                                                                >
                                                                    Save
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="plain"
                                                                    color="neutral"
                                                                    onClick={() => {
                                                                        setEditingBrokerPeerId(null)
                                                                        setEditingBrokerName('')
                                                                    }}
                                                                    disabled={savingBrokerName}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {w.brokerName ? (
                                                                    <Chip size="sm" variant="soft" color="warning">
                                                                        {w.brokerName}
                                                                    </Chip>
                                                                ) : (
                                                                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                                                                        Not assigned
                                                                    </Typography>
                                                                )}
                                                                <IconButton
                                                                    size="sm"
                                                                    variant="plain"
                                                                    color="neutral"
                                                                    disabled={!isActive}
                                                                    onClick={() => {
                                                                        setEditingBrokerPeerId(w.peerId)
                                                                        setEditingBrokerName(w.brokerName || '')
                                                                    }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </Stack>
                                                </Sheet>
                                            )
                                        })}
                                    </>
                                )}

                                {/* Unwatched */}
                                {unwatchedFiltered.length > 0 && (
                                    <>
                                        {watchedFiltered.length > 0 && <Divider sx={{ my: 2 }} />}
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                            <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                                                Available Groups ({unwatchedFiltered.length})
                                            </Typography>
                                            <Button
                                                size="sm"
                                                variant="outlined"
                                                color="primary"
                                                disabled={!isActive}
                                                onClick={async () => {
                                                    resetModalAlerts()
                                                    try {
                                                        const result = await watchAllChats(phone)
                                                        if (!result?.ok) throw new Error(result?.error || 'Failed to watch all')
                                                        const added = result.data?.added || 0
                                                        setModalMessage(`Added ${added} group(s)/channel(s) to watch list`)
                                                        const items = await listWatched(phone)
                                                        handleWatchedUpdate(items)
                                                    } catch (e) {
                                                        setModalError((e as Error)?.message || 'Failed to watch all')
                                                    }
                                                }}
                                            >
                                                Watch All
                                            </Button>
                                        </Stack>
                                        {unwatchedFiltered.map((d) => (
                                            <Sheet
                                                key={d.peerId}
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 'md',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                }}
                                            >
                                                <Stack sx={{ flex: 1 }}>
                                                    <Typography level="title-sm">{d.title || d.username || 'Untitled'}</Typography>
                                                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                                                        {d.username && (
                                                            <Chip size="sm" variant="soft" color="neutral">
                                                                @{d.username}
                                                            </Chip>
                                                        )}
                                                        <Chip size="sm" variant="soft">
                                                            {d.isChannel ? 'Channel' : 'Group'}
                                                        </Chip>
                                                    </Stack>
                                                </Stack>
                                                <Switch
                                                    checked={false}
                                                    disabled={!isActive}
                                                    onChange={async () => {
                                                        if (!isActive) return
                                                        resetModalAlerts()
                                                        try {
                                                            await addWatchedChat({
                                                                phone,
                                                                peerId: d.peerId,
                                                                username: d.username,
                                                                title: d.title,
                                                                isChannel: d.isChannel,
                                                            })
                                                            const groupName = d.title || d.username || 'group'
                                                            setModalMessage(`Added "${groupName}" to watch list`)
                                                            const items = await listWatched(phone)
                                                            handleWatchedUpdate(items)
                                                        } catch (e) {
                                                            setModalError((e as Error)?.message || 'Failed to add to watch list')
                                                        }
                                                    }}
                                                />
                                            </Sheet>
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    )
}
